import { FC } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PromptType, StudioPrompt } from '../../../types/StudioPrompt';
import { getPromptCategoryChipSx, getPromptTypeChipSx } from './studioPromptChipStyles';

interface StudioPromptOptionItemProps {
    option: StudioPrompt;
    hidePromptTypeChip?: boolean;
}

export const StudioPromptOptionItem: FC<StudioPromptOptionItemProps> = ({
    option,
    hidePromptTypeChip = false,
}) => {
    const { t } = useTranslation();

    const getPromptTypeLabel = (type: number) => {
        switch (type) {
            case PromptType.AskilıSecim:
                return t('studyoPrompt.promptType.askiliSecim');
            case PromptType.ModelliCekim:
                return t('studyoPrompt.promptType.modelliCekim');
            case PromptType.HerIkisi:
                return t('studyoPrompt.promptType.herIkisi');
            default:
                return '';
        }
    };

    const typeLabel = getPromptTypeLabel(option.promptType);
    const showTypeChip = !hidePromptTypeChip && Boolean(typeLabel);

    const normalizedCategory = option.category?.trim().toLocaleLowerCase('tr-TR') ?? '';
    const isRedundantAskiliCategory =
        hidePromptTypeChip && (normalizedCategory === 'askılı' || normalizedCategory === 'askili');
    const showCategoryChip = Boolean(option.category) && !isRedundantAskiliCategory;
    const showChips = showTypeChip || showCategoryChip;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.5, py: 0.25 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    width: '100%',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                }}
            >
                {option.name}
            </Typography>

            {showChips && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    {showTypeChip && (
                        <Chip
                            label={typeLabel}
                            size="small"
                            variant="outlined"
                            sx={getPromptTypeChipSx()}
                        />
                    )}
                    {showCategoryChip && (
                        <Chip
                            label={option.category}
                            size="small"
                            variant="outlined"
                            sx={getPromptCategoryChipSx(option.category)}
                        />
                    )}
                </Box>
            )}

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4,
                }}
            >
                {option.promptText}
            </Typography>
        </Box>
    );
};
